import { useState, useContext, useEffect} from "react"

import FieldMapper from "partials/census-sync/field-mapper"
import ProgressLoader from "partials/progress-loader"
import DestinationObjectSelector from "partials/census-sync/destination-object-selector"
import SyncBehaviorSelector from "./sync-behavior-selector"
import SessionContext from "SessionContext";

const STEPS = {
  SELECT_DESTINATION_OBJECT: "SELECT_DESTINATION_OBJECT",
  SELECT_SYNC_BEHAVIOR: "SELECT_SYNC_BEHAVIOR",
  MAP_FIELDS: "MAP_FIELDS",
  CREATING: "CREATING",
}

export default function CensusDestinationSync({ sourceId, selectedDestination, destinationId, order, onExit, onComplete, onMappingStatusChange }) {
  const session = useContext(SessionContext);
  const [currentStep, setCurrentStep] = useState(null)
  const [selectedSourceTable, setSelectedSourceTable] = useState(null)
  const [selectedDestinationObject, setSelectedDestinationObject] = useState(selectedDestination || null);
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [destinationObjectDetails, setDestinationObjectDetails] = useState(null)
  const [advancedConfig, setAdvancedConfig] = useState({})
  const [hasMappingChanges, setHasMappingChanges] = useState(false);

  // Notify parent when mapping changes status changes
  useEffect(() => {
    onMappingStatusChange?.(hasMappingChanges);
  }, [hasMappingChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDestinationObjectSelect = (object) => {
    setSelectedDestinationObject(object)
    setDestinationObjectDetails(null) // Clear old destination details when changing destination
    setHasMappingChanges(false) // Reset mapping changes when destination changes
    setCurrentStep(STEPS.SELECT_SYNC_BEHAVIOR)
  }

  const handleOperationSelect = (operation, objectDetails, config = {}) => {
    setSelectedOperation(operation)
    setDestinationObjectDetails(objectDetails)
    setAdvancedConfig(config)
    setHasMappingChanges(false) // Reset mapping changes when operation changes
    setCurrentStep(STEPS.MAP_FIELDS)
  }

  const handleCancel = () => {
    onExit({status:"not_created"});
  }

  const handleFieldMappingComplete = async (mappings) => {
    setCurrentStep(STEPS.CREATING)
    // Filter out empty/null values from advanced config
    const cleanAdvancedConfig = Object.fromEntries(
      Object.entries(advancedConfig).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );

    session.command('census/create-sync', {
          id: sourceId,
          census_source_id: sourceId,
          order_id: order?.id,
          source_table_name: selectedSourceTable.table_name,
          source_table_catalog: selectedSourceTable.table_catalog,
          source_table_schema: selectedSourceTable.table_schema,
          census_destination_id: destinationId,
          destination_object: selectedDestinationObject.full_name,
          operation: selectedOperation,
          field_mappings: mappings,
          field_order: "mapping_order",
          ...(Object.keys(cleanAdvancedConfig).length > 0 && { advanced_configuration: cleanAdvancedConfig }),
    }).then((data) => {
        // Call onComplete callback if provided
        if (data.status === 'created') {
          onExit(data)
          // Reset wizard to initial state
          setCurrentStep(STEPS.SELECT_DESTINATION_OBJECT)
          setSelectedDestinationObject(null)
          setSelectedOperation(null)
          setDestinationObjectDetails(null)
          setAdvancedConfig({})
        }
    }).catch((err) => {
      console.error("Error creating sync:", err);
      setCurrentStep(STEPS.MAP_FIELDS)
    });
  }

  useEffect(() => {
    if(!sourceId) { return; }
    setTimeout(() => { 
      session.query('census/get-source-table', { census_source_id: sourceId, order_id: order?.id })
        .then((response) => {
          setSelectedSourceTable(response);
          setCurrentStep(STEPS.SELECT_DESTINATION_OBJECT)
        })
        .catch((err) => {
          console.error("Error fetching destination objects:", err);
        });
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[sourceId]);

  if (currentStep === STEPS.CREATING) {
    return (
      <div className="progress-loader-container">
        <ProgressLoader /> 
        <p>Creating your sync...</p>
      </div>
    )
  }

  return (
    <div className="destination-sync-container">
      {(currentStep === STEPS.SELECT_DESTINATION_OBJECT ||
        currentStep === STEPS.SELECT_SYNC_BEHAVIOR ||
        currentStep === STEPS.MAP_FIELDS
      ) && sourceId && (
        <DestinationObjectSelector
          sourceId={sourceId}
          destinationId={destinationId}
          destinationName={selectedDestination?.name}
          selectedObject={selectedDestinationObject}
          hasMappingChanges={hasMappingChanges}
          onSelect={handleDestinationObjectSelect}
        />
      )}

      {(currentStep === STEPS.SELECT_SYNC_BEHAVIOR ||
        currentStep === STEPS.MAP_FIELDS
      ) && (
        <>
          <SyncBehaviorSelector
            destinationId={destinationId}
            destinationObjectFullName={selectedDestinationObject.full_name}
            destinationObject={selectedDestinationObject}
            hasMappingChanges={hasMappingChanges}
            onSelect={handleOperationSelect}
          />
        </>
      )}

      {currentStep === STEPS.MAP_FIELDS && (
        <FieldMapper
          sourceId={sourceId}
          sourceTable={selectedSourceTable}
          orderId={order?.id}
          orderName={order?.name || `#${order?.id}`}
          destinationName={selectedDestination?.name}
          destinationObject={selectedDestinationObject}
          destinationId={destinationId}
          destinationObjectFullName={selectedDestinationObject.full_name}
          destinationObjectFields={destinationObjectDetails?.fields || []}
          allowCustomFields={destinationObjectDetails?.allow_custom_fields || false}
          supportsFieldOrdering={destinationObjectDetails?.supports_field_ordering || false}
          selectedOperation={selectedOperation}
          onComplete={handleFieldMappingComplete}
          onFieldsRefresh={(updatedFields) => {
            setDestinationObjectDetails({ ...destinationObjectDetails, fields: updatedFields })
          }}
          onMappingChange={() => setHasMappingChanges(true)}
          hasMappingChanges={hasMappingChanges}
          onCancel={handleCancel}
        />
      )}
    </div>
  )
}
